import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import content from '../config/content.json';
import { 
  FaBed, FaFire, FaTint, FaBolt, FaParking, FaShieldAlt,
  FaPlay, FaPause, FaChevronLeft, FaChevronRight
} from 'react-icons/fa';
import { GiWashingMachine } from 'react-icons/gi';

const iconMap = {
  bed: FaBed,
  fire: FaFire,
  washing: GiWashingMachine,
  water: FaTint,
  power: FaBolt,
  parking: FaParking,
  shield: FaShieldAlt
};

function Home() {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);

  // Detect mobile device
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768 || 'ontouchstart' in window);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  // Generate image paths dynamically from config
  const { imageCount, imageFormat, imagePath, imagePrefix } = content.gallery;
  const images = Array.from(
    { length: imageCount }, 
    (_, i) => `${process.env.PUBLIC_URL}${imagePath}${imagePrefix}${i + 1}.${imageFormat}`
  );

  useEffect(() => {
    if (!isPlaying) return;
    
    const interval = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % images.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [images.length, isPlaying]);

  const goToNext = () => {
    setCurrentImageIndex((prev) => (prev + 1) % images.length);
  };

  const goToPrevious = () => {
    setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  const togglePlayPause = () => {
    setIsPlaying(!isPlaying);
  };

  const openFullscreen = () => {
    setIsFullscreen(true);
    setIsPlaying(false);
  };

  const closeFullscreen = () => {
    setIsFullscreen(false);
    setIsPlaying(true);
  };

  useEffect(() => {
    const handleKeyPress = (e) => {
      if (!isFullscreen) return;
      if (e.key === 'Escape') closeFullscreen();
      if (e.key === 'ArrowLeft') goToPrevious();
      if (e.key === 'ArrowRight') goToNext();
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [isFullscreen]);

  // Minimum swipe distance (in px)
  const minSwipeDistance = 50;

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    
    if (isLeftSwipe) {
      goToNext();
    }
    if (isRightSwipe) {
      goToPrevious();
    }
  };

  return (
    <div className="home">
      <section className="hero">
        <motion.div 
          className="hero-content"
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <h1>{content.siteInfo.tagline}</h1>
          <p>{content.siteInfo.description}</p>
          <div className="hero-buttons">
            <Link to="/contact" className="btn btn-primary">Get in Touch</Link>
            <Link to="/about" className="btn btn-secondary">Learn More</Link>
          </div>
        </motion.div>
        <div className="hero-image">
          <img src={process.env.PUBLIC_URL + "/photos/thendral_3d.png"} alt="Thendral Mansion" />
        </div>
      </section>

      <section className="facilities">
        <div className="container">
          <motion.h2
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            World-Class Facilities
          </motion.h2>
          <div className="facilities-grid">
            {content.facilities.map((facility, index) => {
              const Icon = iconMap[facility.icon];
              return (
                <motion.div 
                  key={index}
                  className="facility-card"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1, duration: 0.5 }}
                >
                  <div className="facility-icon">
                    <Icon />
                  </div>
                  <h3>{facility.title}</h3>
                  <p>{facility.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="gallery">
        <div className="container">
          <h2>Photo Gallery</h2>
          <div className="carousel">
            <div className="carousel-container">
              {images.map((img, index) => (
                <img
                  key={index}
                  src={img}
                  alt={`Thendral Mansion ${index + 1}`}
                  className={index === currentImageIndex ? 'active' : ''}
                  loading="lazy"
                  decoding="async"
                  style={{ 
                    zIndex: index === currentImageIndex ? 1 : 0,
                    pointerEvents: index === currentImageIndex ? 'auto' : 'none'
                  }}
                  onClick={openFullscreen}
                />
              ))}
              
              <button 
                className="carousel-control prev" 
                onClick={goToPrevious}
                aria-label="Previous image"
              >
                <FaChevronLeft />
              </button>
              
              <button 
                className="carousel-control next" 
                onClick={goToNext}
                aria-label="Next image"
              >
                <FaChevronRight />
              </button>
              
              <button 
                className="carousel-control play-pause" 
                onClick={togglePlayPause}
                aria-label={isPlaying ? "Pause" : "Play"}
              >
                {isPlaying ? <FaPause /> : <FaPlay />}
              </button>

              <button 
                className="carousel-control expand" 
                onClick={openFullscreen}
                aria-label="Expand to fullscreen"
              >
                <span>⛶</span>
              </button>
            </div>
            
            <div className="carousel-dots">
              {images.map((_, index) => (
                <button
                  key={index}
                  className={index === currentImageIndex ? 'active' : ''}
                  onClick={() => setCurrentImageIndex(index)}
                  aria-label={`Go to image ${index + 1}`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Fullscreen Modal */}
      {isFullscreen && (
        <div 
          className="fullscreen-modal"
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
        >
          <button 
            className="fullscreen-close" 
            onClick={closeFullscreen}
            aria-label="Close fullscreen"
          >
            ✕
          </button>
          
          <div className="fullscreen-container">
            {images.map((img, index) => (
              <img
                key={index}
                src={img}
                alt={`Thendral Mansion ${index + 1}`}
                className={index === currentImageIndex ? 'active' : ''}
              />
            ))}
          </div>

          <button 
            className="fullscreen-control prev" 
            onClick={goToPrevious}
            aria-label="Previous image"
          >
            <FaChevronLeft />
          </button>
          
          <button 
            className="fullscreen-control next" 
            onClick={goToNext}
            aria-label="Next image"
          >
            <FaChevronRight />
          </button>

          <div className="fullscreen-counter">
            {currentImageIndex + 1} / {images.length}
          </div>

          <div className="fullscreen-dots">
            {images.map((_, index) => (
              <button
                key={index}
                className={index === currentImageIndex ? 'active' : ''}
                onClick={() => setCurrentImageIndex(index)}
                aria-label={`Go to image ${index + 1}`}
              />
            ))}
          </div>
        </div>
      )}

      <section className="cta">
        <div className="container">
          <h2>Ready to Make Thendral Mansion Your Home?</h2>
          <p>Contact us today to schedule a visit or book your room</p>
          <Link to="/contact" className="btn btn-primary">Contact Us Now</Link>
        </div>
      </section>
    </div>
  );
}

export default Home;
